import { useAppSelector } from 'store/hooks';
import React from 'react';
import { Box, Button, Grid, InputLabel, Link, styled, Typography } from '@mui/material';
import { Row } from 'ui/Flex';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { MuiForm } from 'lib/Form';
import {
    CANCELLATION_REASON_OPTIONS,
    CLIENT_PLATFORM_OPTIONS,
    EActionTypeGroup,
    EChannel,
    EClientPlatform,
    ETimeOfDay,
    getActionTypeGroup,
    ORDER_PAYMENT_TYPE_OPTIONS
} from 'components/audience/models/addTriggersModel';
import * as Yup from 'yup';
import { Form, FormikHelpers } from 'formik';
import { Audience } from 'components/audience/models/segmentModel';
import {
    renderAmountField,
    renderDynamicSingleSelect,
    renderChannelSelect,
    renderMonthOfYearSelect,
    renderPrimaryPlatformSelect,
    renderScenarioSelect,
    renderTimeOfDaySelect,
    renderWeekOfYearInput,
    renderDynamicMultiSelect,
    ExpandableDynamicChipInput,
    renderDynamicTextField,
    renderDynamicNumberField,
    renderDayOfWeekSelect,
    renderDynamicDaysOfWeekSelect,
    renderDynamicDateRangeSelect,
    SubscriptionPricingPlanSelect
} from 'components/audience/utils/addTriggerUtils';
import { OrderScenario } from 'components/order/model/Order';
import { ActionType } from 'components/actions/models/Action';
import { getActionTypeOptions } from 'components/actions/utils';
import { EPrimaryPlatform } from 'components/customers/models/Customer';
import { Option } from 'lib/types';
import { AdjustmentScheme } from 'components/adjustment-schemes/adjustmentSchemeModel';
import { SubscriptionScheme } from 'components/subscription-schemes/subscriptionSchemeModel';
import { Location } from 'components/location/models/LocationModel';
import { Perk } from 'components/loyalty/models/PerkModel';
import { EPaymentProvider } from 'components/settings/PaymentsSettings';
import { isString } from 'lib/typeguards';
import { ApplicationState } from 'store/store';
import { MARKETING_VOUCHERS } from 'config/routes';
import { AllConditionPredicates } from 'components/rules/rules.types';

const PREFIX = 'AudienceAddTriggersForm';

const classes = {
    disabledInput: `${PREFIX}-disabledInput`
};

const StyledForm = styled(Form)({
    [`& .${classes.disabledInput}.MuiInput-underline.Mui-disabled`]: {
        '&:before': {
            borderBottomStyle: 'solid'
        }
    }
});

export interface IAudienceAddTriggersFormData {
    actionType?: ActionType;
    locationIds?: string[];
    dayPart?: ETimeOfDay;
    dayOfWeek?: string;
    weekOfYear?: string;
    monthOfYear?: string;
    primaryPlatform?: EPrimaryPlatform;
    amountPredicate?: AllConditionPredicates;
    amount?: string;
    scenario?: OrderScenario;
    channel?: EChannel;
    audienceIds?: string[];
    schemeId?: string;
    perkId?: string;
    voucherId?: string;
    clientPlatforms?: string[];
    categoryIds?: string[];
    productIds?: string[];
    paymentMethod?: string;
    emailEndsWith?: string;
    favouriteLocationIds?: string[];
    // booking specific conditions, TODO: might require a change when the platform is ready
    arrivalDaysOfWeek?: string[];
    arrivalDateFrom?: Date;
    arrivalDateTo?: Date;
    departureDaysOfWeek?: string[];
    departureDateFrom?: Date;
    departureDateTo?: Date;
    stayLength?: string;
    stayLengthPredicate?: AllConditionPredicates;
    status?: string;
    guestsNumber?: string;
    guestsNumberPredicate?: AllConditionPredicates;
    bookerEmailEndsWith?: string;
    guestEmailEndsWith?: string;
    rateCode?: string;
    costPredicate?:
        | 'LESSER'
        | 'EQUAL'
        | 'NOT_EQUAL'
        | 'IN'
        | 'NOT_IN'
        | 'EQ'
        | 'NEQ'
        | 'NIN'
        | 'GREATER'
        | 'GREATER_THAN_OR_EQUAL'
        | 'LESS_THAN_OR_EQUAL'
        | 'GT'
        | 'LT'
        | 'GTE'
        | 'LTE';
    cost?: string;
    // subscription specific conditions
    subscriptionSchemeId?: string;
    stripePriceId?: string;
    cancellationReason?: string;
}

interface IAudienceTriggersBaseFormProps {
    formTitle: React.ReactNode;
    audienceData: Partial<Audience>;
    onSubmit: (
        id: string,
        data: IAudienceAddTriggersFormData,
        formikHelpers?: FormikHelpers<IAudienceAddTriggersFormData>
    ) => void;
    locations: Array<Location>;
    audiences: Array<Audience>;
    perks: Array<Perk>;
    adjustmentSchemes: Array<AdjustmentScheme>;
    subscriptionSchemes: Array<SubscriptionScheme>;
    onSkip: () => void;
}

const validationSchema = Yup.object().shape({
    actionType: Yup.string().required('This field is required.'),
    audienceIds: Yup.array().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.SIXTH,
        then: Yup.array().min(1, 'At least one audience is required')
    }),
    schemeId: Yup.string().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.SEVENTH,
        then: Yup.string().required(' ')
    }),
    perkId: Yup.string().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType &&
            (getActionTypeGroup(actionType) === EActionTypeGroup.EIGHTH ||
                getActionTypeGroup(actionType) === EActionTypeGroup.TENTH),
        then: Yup.string().required(' ')
    }),
    voucherId: Yup.string().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.NINTH,
        then: Yup.string().required('Please enter a voucher ID')
    }),
    locationIds: Yup.array().optional(),
    clientPlatforms: Yup.array().optional(),
    arrivalDateFrom: Yup.mixed().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH,
        then: Yup.mixed().notRequired()
    }),
    arrivalDateTo: Yup.mixed().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH,
        then: Yup.mixed().notRequired()
    }),
    departureDateFrom: Yup.mixed().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH,
        then: Yup.mixed().notRequired()
    }),
    departureDateTo: Yup.mixed().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH,
        then: Yup.mixed().notRequired()
    })
});

const initialFormValues: IAudienceAddTriggersFormData = {
    actionType: undefined,
    locationIds: [],
    audienceIds: [],
    clientPlatforms: [],
    categoryIds: [],
    productIds: [],
    arrivalDaysOfWeek: [],
    departureDaysOfWeek: [],
    arrivalDateFrom: null,
    arrivalDateTo: null,
    departureDateFrom: null,
    departureDateTo: null
};

export const AudienceTriggersBaseForm = ({
    onSkip,
    audienceData: { _id },
    onSubmit,
    locations,
    audiences,
    perks,
    adjustmentSchemes,
    subscriptionSchemes,
    formTitle
}: IAudienceTriggersBaseFormProps) => {
    const { paymentProvider, subscriptionsEnabled } = useAppSelector(
        (state: ApplicationState) => state?.settings?.settings
    );
    const isStripe = React.useMemo(
        () => isString(paymentProvider) && paymentProvider === EPaymentProvider.Stripe,
        [paymentProvider]
    );
    const actionTypeOptions = React.useMemo(
        () =>
            getActionTypeOptions({
                subscriptionsEnabled: subscriptionsEnabled && subscriptionSchemes.length > 0
            }),
        [subscriptionsEnabled, subscriptionSchemes.length]
    );
    const computedClientPlatformOptions = React.useMemo(() => {
        if (!isStripe) {
            return CLIENT_PLATFORM_OPTIONS.filter(option => option.value !== EClientPlatform.TERMINAL);
        }
        return CLIENT_PLATFORM_OPTIONS;
    }, [isStripe]);

    const locationOptions = React.useMemo(
        () =>
            locations.map<Option>(location => ({
                value: location._id,
                label: location.title
            })),
        [locations]
    );
    const audienceOptions = React.useMemo(
        () =>
            audiences.map<Option>(audience => ({
                value: audience._id,
                label: audience.title
            })),
        [audiences]
    );

    const adjustmentSchemeOptions = React.useMemo(
        () =>
            adjustmentSchemes.map<Option>(adjustmentScheme => ({
                value: adjustmentScheme.schemeId,
                label: `${adjustmentScheme.title} (${adjustmentScheme.schemeId})`
            })),
        [adjustmentSchemes]
    );

    const perkOptions = React.useMemo(
        () =>
            perks.map<Option>(perk => ({
                value: perk._id,
                label: `${perk.internalTitle || perk.title} (${perk._id})`
            })),
        [perks]
    );

    const subscriptionSchemeOptions = React.useMemo(
        () =>
            subscriptionSchemes.map<Option>(scheme => ({
                value: scheme._id,
                label: scheme.title
            })),
        [subscriptionSchemes]
    );

    const voucherIdDescription = (
        <Typography variant="caption">
            To view and copy your voucher ID, click{' '}
            <Link href={MARKETING_VOUCHERS} target="_blank">
                here
            </Link>{' '}
            to open your vouchers in a new window.
        </Typography>
    );

    const handleSubmit = React.useCallback(
        (
            values: IAudienceAddTriggersFormData,
            formikHelpers: FormikHelpers<IAudienceAddTriggersFormData>
        ) => {
            onSubmit(_id, values, formikHelpers);
        },
        [onSubmit, _id]
    );

    return (
        <MuiForm
            validationSchema={validationSchema}
            initialValues={initialFormValues}
            onSubmit={handleSubmit}
        >
            {({ submitForm, isSubmitting, values: { actionType }, isValid }) => (
                <StyledForm>
                    <Grid container spacing={2}>
                        <Grid size={12}>{formTitle}</Grid>
                        <Grid size={12}>
                            <Grid container alignItems="center">
                                <Grid size={4}>
                                    <InputLabel>Action Type</InputLabel>
                                </Grid>
                                <Grid size={8}>
                                    <SelectFormField options={actionTypeOptions} name="actionType" />
                                </Grid>
                            </Grid>
                        </Grid>
                        {getActionTypeGroup(actionType) === EActionTypeGroup.SIXTH &&
                            renderDynamicMultiSelect('audienceIds', 'Audiences', audienceOptions, true)}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.SEVENTH &&
                            renderDynamicSingleSelect(
                                'schemeId',
                                'Adjustment Scheme',
                                adjustmentSchemeOptions,
                                true
                            )}
                        {(getActionTypeGroup(actionType) === EActionTypeGroup.EIGHTH ||
                            getActionTypeGroup(actionType) === EActionTypeGroup.TENTH) && (
                            <>{renderDynamicSingleSelect('perkId', 'Perk', perkOptions, true)}</>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.NINTH && (
                            <>
                                {renderDynamicTextField(
                                    'voucherId',
                                    'Voucher ID',
                                    true,
                                    '',
                                    voucherIdDescription
                                )}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.ELEVENTH && (
                            <>
                                <Grid size={12}>
                                    <Typography variant="body1">Basket contains:</Typography>
                                </Grid>
                                <ExpandableDynamicChipInput
                                    name="categoryIds"
                                    label="Category ID(s)"
                                    placeholder="e.g. 5, 6, 7"
                                    isRequired={false}
                                    isExpandable
                                />
                                <ExpandableDynamicChipInput
                                    name="productIds"
                                    label="Product ID(s)"
                                    placeholder="e.g. 100, 101, 102"
                                    isRequired={false}
                                    isExpandable
                                />
                                {renderDynamicSingleSelect(
                                    'paymentMethod',
                                    'Payment Method',
                                    ORDER_PAYMENT_TYPE_OPTIONS,
                                    false
                                )}
                            </>
                        )}
                        {getActionTypeGroup(actionType) !== EActionTypeGroup.FIFTEENTH &&
                            getActionTypeGroup(actionType) !== EActionTypeGroup.SIXTEENTH &&
                            renderDynamicMultiSelect(
                                'clientPlatforms',
                                'Channel',
                                computedClientPlatformOptions,
                                false
                            )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FIRST && (
                            <>
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.SECOND && (
                            <>
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.THIRD && (
                            <>
                                {renderAmountField()}
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                                {renderScenarioSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FOURTH && (
                            <>
                                {renderAmountField()}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                                {renderChannelSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FIFTH && (
                            <>
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                            </>
                        )}
                        {(getActionTypeGroup(actionType) === EActionTypeGroup.EIGHTH ||
                            getActionTypeGroup(actionType) === EActionTypeGroup.NINTH) && (
                            <>
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.TENTH && (
                            <>
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                            </>
                        )}

                        {getActionTypeGroup(actionType) === EActionTypeGroup.ELEVENTH && (
                            <>
                                {renderAmountField()}
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                                {renderScenarioSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.TWELFTH && (
                            <>
                                {renderDynamicTextField(
                                    'emailEndsWith',
                                    'Email ends with',
                                    false,
                                    'e.g. ac.uk'
                                )}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH && (
                            <>
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderDynamicDateRangeSelect(
                                    'arrivalDateFrom',
                                    'arrivalDateTo',
                                    'Arrival date'
                                )}
                                {renderDynamicDateRangeSelect(
                                    'departureDateFrom',
                                    'departureDateTo',
                                    'Departure date'
                                )}
                                {renderDynamicDaysOfWeekSelect('arrivalDaysOfWeek', 'Arrival day')}
                                {renderDynamicDaysOfWeekSelect('departureDaysOfWeek', 'Departure day')}
                                {renderDynamicNumberField(
                                    'stayLength',
                                    'stayLengthPredicate',
                                    'Stay length (nights)'
                                )}
                                {renderDynamicTextField('status', 'Booking status', false, '')}
                                {renderDynamicTextField(
                                    'bookerEmailEndsWith',
                                    'Booker email ends with',
                                    false,
                                    'e.g. ac.uk'
                                )}
                                {renderDynamicNumberField(
                                    'guestsNumber',
                                    'guestsNumberPredicate',
                                    'Number of Guests'
                                )}
                                {/* TODO: waiting for platform to support this field */}
                                {/* {renderDynamicTextField(
                                    'guestEmailEndsWith',
                                    'Guest email ends with',
                                    false,
                                    'e.g. ac.uk'
                                )} */}
                                {renderDynamicTextField('rateCode', 'Rate/Package code', false, '')}
                                {renderDynamicNumberField('cost', 'costPredicate', 'Total cost')}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FOURTEENTH && (
                            <>
                                {renderDynamicMultiSelect(
                                    'favouriteLocationIds',
                                    'Favourite locations',
                                    locationOptions,
                                    false
                                )}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FIFTEENTH && (
                            <>
                                {renderDynamicSingleSelect(
                                    'subscriptionSchemeId',
                                    'Subscription Scheme',
                                    subscriptionSchemeOptions,
                                    false
                                )}
                                <SubscriptionPricingPlanSelect subscriptionSchemes={subscriptionSchemes} />
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.SIXTEENTH && (
                            <>
                                {renderDynamicSingleSelect(
                                    'subscriptionSchemeId',
                                    'Subscription Scheme',
                                    subscriptionSchemeOptions,
                                    false
                                )}
                                <SubscriptionPricingPlanSelect subscriptionSchemes={subscriptionSchemes} />
                                {renderDynamicSingleSelect(
                                    'cancellationReason',
                                    'Cancellation Reason',
                                    CANCELLATION_REASON_OPTIONS,
                                    false
                                )}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                            </>
                        )}

                        <Grid size={12}>
                            <Row gutter align="flex-end" valign="center">
                                <Box display="flex" marginTop={2}>
                                    <Box marginRight={1}>
                                        <Button
                                            onClick={onSkip}
                                            disabled={isSubmitting}
                                            variant="outlined"
                                            color="primary"
                                        >
                                            SKIP
                                        </Button>
                                    </Box>
                                    <LoadingButton
                                        variant="contained"
                                        color="primary"
                                        disabled={!isValid || isSubmitting}
                                        loading={isSubmitting}
                                        onClick={submitForm}
                                    >
                                        SAVE
                                    </LoadingButton>
                                </Box>
                            </Row>
                        </Grid>
                    </Grid>
                </StyledForm>
            )}
        </MuiForm>
    );
};
